import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer, Filter } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { toast } from "sonner";

// Iter187 — QR Kiosk. Renders one QR code per player pointing at their
// self-service link. Coach hits Print and pins the sheet at the desk;
// players scan their own square and hit Available / Resting from their
// phone without ever tapping a shared iPad.
//
// Iter188 — Filters. Coach can narrow the printed sheet to a single
// colour band and / or a single arrival day (bucketed by `created_at`
// calendar date). Filters live in URL params so they survive Print /
// share links and can be deep-linked from Player Manager.
const BAND_STYLES = {
  green: "border-[#4CAF50] text-[#4CAF50]",
  blue: "border-[#66C2FF] text-[#66C2FF]",
  red: "border-[#FF4500] text-[#FF4500]",
  spare: "border-[#FFB020] text-[#FFB020]",
  unrated: "border-[#525252] text-[#525252]",
};

function dayKey(iso) {
  if (!iso) return "unknown";
  try {
    return new Date(iso).toISOString().slice(0, 10); // YYYY-MM-DD
  } catch {
    return "unknown";
  }
}

function dayLabel(iso) {
  if (!iso || iso === "unknown") return "Unknown date";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short", day: "numeric", month: "short",
    });
  } catch {
    return iso;
  }
}

export default function FestivalKiosk() {
  const { id } = useParams();
  const [festival, setFestival] = useState(null);
  const [players, setPlayers] = useState([]);
  const [params, setParams] = useSearchParams();
  const bandFilter = params.get("band") || "";
  const dayFilter = params.get("day") || "";
  const sortBy = params.get("sort") || "";

  useEffect(() => {
    (async () => {
      try {
        const [f, p] = await Promise.all([
          api.get(`/festival/${id}`),
          api.get(`/festival/${id}/players`),
        ]);
        setFestival(f.data);
        setPlayers((p.data.items || []).filter((x) => x.self_service_token));
      } catch {
        toast.error("Failed to load kiosk");
      }
    })();
  }, [id]);

  const bandOptions = useMemo(() => {
    const counts = { green: 0, blue: 0, red: 0, spare: 0, unrated: 0 };
    for (const p of players) counts[p.colour_band || "unrated"] = (counts[p.colour_band || "unrated"] || 0) + 1;
    return counts;
  }, [players]);

  const dayOptions = useMemo(() => {
    const days = new Map();
    for (const p of players) {
      const k = dayKey(p.created_at);
      days.set(k, (days.get(k) || 0) + 1);
    }
    return Array.from(days.entries()).sort(); // sorted by ISO date
  }, [players]);

  const filtered = useMemo(() => {
    const list = players.filter((p) => {
      if (bandFilter && (p.colour_band || "unrated") !== bandFilter) return false;
      if (dayFilter && dayKey(p.created_at) !== dayFilter) return false;
      return true;
    });
    if (!sortBy) return list;
    const [a1, a2] = sortBy === "last" ? ["last_name", "first_name"] : ["first_name", "last_name"];
    const key = (p) => `${p[a1] || ""} ${p[a2] || ""}`.trim().toLowerCase();
    return [...list].sort((a, b) => key(a).localeCompare(key(b)));
  }, [players, bandFilter, dayFilter, sortBy]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  if (!festival) return <div className="p-8 text-sm text-[#A3A3A3]">Loading…</div>;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const activeFilters = [];
  if (bandFilter) activeFilters.push(bandFilter);
  if (dayFilter) activeFilters.push(dayLabel(dayFilter));

  return (
    <div className="max-w-[1200px] mx-auto px-4 py-6" data-testid="festival-kiosk">
      <div className="flex items-center justify-between mb-6 rl-no-print">
        <Link to={`/festival/${id}`} className="text-[11px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> {festival.name}
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="kiosk-print"
          className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold"
          style={{ background: "#CCFF00", color: "#0A0A0A" }}
        >
          <Printer className="w-4 h-4" /> Print sheet
        </button>
      </div>

      <div className="mb-6 rl-no-print">
        <div className="label-mono text-[#CCFF00] mb-1">QR KIOSK</div>
        <h1 className="h-display text-3xl">Player self-service codes</h1>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Showing {filtered.length} of {players.length} · Pin this at check-in.
          {activeFilters.length > 0 && <span className="ml-2 text-[#CCFF00]">Filter: {activeFilters.join(" · ")}</span>}
        </p>
      </div>

      {/* Filter bar */}
      <div className="rl-no-print card-surface p-3 mb-4 flex flex-wrap gap-4 items-center" data-testid="kiosk-filters">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#525252]">
          <Filter className="w-3 h-3" /> Band:
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setFilter("band", "")}
            data-testid="kiosk-band-all"
            className={`px-2 py-1 text-[10px] uppercase tracking-wider border ${!bandFilter ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#A3A3A3]"}`}
          >
            All · {players.length}
          </button>
          {["green", "blue", "red", "spare", "unrated"].map((b) => (
            bandOptions[b] > 0 && (
              <button
                key={b}
                type="button"
                onClick={() => setFilter("band", b)}
                data-testid={`kiosk-band-${b}`}
                className={`px-2 py-1 text-[10px] uppercase tracking-wider border ${bandFilter === b ? BAND_STYLES[b] : "border-[#262626] text-[#A3A3A3]"}`}
              >
                {b} · {bandOptions[b]}
              </button>
            )
          ))}
        </div>
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#525252]">
          Sort:
          <select
            value={sortBy}
            onChange={(e) => setFilter("sort", e.target.value)}
            data-testid="kiosk-sort"
            className="bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] focus:outline-none px-2 py-1 text-[11px] normal-case tracking-normal text-white"
          >
            <option value="">Default order</option>
            <option value="first">First name A–Z</option>
            <option value="last">Last name A–Z</option>
          </select>
        </label>
        {dayOptions.length > 1 && (
          <>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#525252] ml-2">
              Day:
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFilter("day", "")}
                data-testid="kiosk-day-all"
                className={`px-2 py-1 text-[10px] uppercase tracking-wider border ${!dayFilter ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#A3A3A3]"}`}
              >
                Any day
              </button>
              {dayOptions.map(([k, count]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFilter("day", k)}
                  data-testid={`kiosk-day-${k}`}
                  className={`px-2 py-1 text-[10px] uppercase tracking-wider border ${dayFilter === k ? "border-[#CCFF00] text-[#CCFF00]" : "border-[#262626] text-[#A3A3A3]"}`}
                >
                  {dayLabel(k)} · {count}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="card-surface p-8 text-center" data-testid="kiosk-empty">
          <div className="h-display text-xl">
            {players.length === 0 ? "No players yet" : "No players match this filter"}
          </div>
          <p className="text-sm text-[#A3A3A3] mt-1">
            {players.length === 0
              ? "Import a roster or add walk-ins on the Player Manager page."
              : "Try a different band / day or clear filters up top."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="kiosk-grid">
          {filtered.map((p) => {
            const url = `${origin}/festival/${id}/me/${p.self_service_token}`;
            return (
              <div key={p.id} data-testid={`kiosk-card-${p.id}`} className="bg-white text-black p-3 flex flex-col items-center border-2 border-black rl-print-card">
                <div className="text-[10px] uppercase tracking-widest text-[#525252] w-full text-center">Rally-Lab · {festival.name}</div>
                <div className="text-sm font-bold text-center mt-1">{p.first_name} {p.last_name}</div>
                {p.colour_band && (
                  <div className="text-[9px] uppercase tracking-wider text-[#525252]">{p.colour_band} band</div>
                )}
                <div className="my-3">
                  <QRCodeSVG value={url} size={280} level="H" includeMargin={false} shapeRendering="crispEdges" style={{ width: 140, height: 140, imageRendering: "pixelated" }} />
                </div>
                <div className="text-[9px] text-[#525252] text-center break-all leading-tight">
                  Scan → tap Available
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
